import { useEffect, useRef, useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { packages } from "@/mocks/products";
import { Link } from "react-router-dom";

const nsfClaims = [
  "Arsenic (Pentavalent) ≤300 ppb Reduction",
  "Barium Reduction",
  "Cadmium Reduction",
  "Chromium (Hexavalent) Reduction",
  "Chromium (Trivalent) Reduction",
  "Copper Reduction",
  "Fluoride Reduction",
  "Lead Reduction",
  "Nitrate/Nitrite Reduction",
  "Radium 226/228 Reduction",
  "Selenium Reduction",
  "TDS Reduction",
];

export default function PackagesPage() {
  const [nsfModalOpen, setNsfModalOpen] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const pkgRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (hash) {
      const index = packages.findIndex((p) => p.id === hash);
      const el = pkgRefs.current[index];
      if (index !== -1 && el) {
        const navbarOffset = 100;
        const top = el.getBoundingClientRect().top + window.scrollY - navbarOffset;
        window.scrollTo({ top, behavior: "smooth" });
      }
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll(".reveal");
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-dark-bg">
      <Navbar />
      <main>
        {/* Header */}
        <section className="pt-28 md:pt-36 pb-16 md:pb-20 bg-dark-bg">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-4xl">
            <p className="text-xs md:text-sm font-sans font-semibold tracking-[0.2em] text-gold uppercase mb-4">
              Our Collection
            </p>
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-medium text-charcoal-50 leading-tight tracking-tight-xl mb-6">
              Purification Packages
            </h1>
            <p className="text-base md:text-lg text-charcoal-300 font-sans leading-relaxed max-w-2xl">
              Four tiers designed for different homes, lifestyles, and water quality needs.
              Every system is backed by our support and manufacturer warranty.
            </p>
          </div>
        </section>

        {/* Package Detail Cards */}
        <section ref={sectionRef} className="pb-24 md:pb-32 bg-dark-bg">
          <div className="w-full px-6 md:px-10 lg:px-16 flex flex-col gap-16 md:gap-20">
            {packages.map((pkg, index) => (
              <div
                key={pkg.id}
                ref={(el) => { pkgRefs.current[index] = el; }}
                id={pkg.id}
                className={`reveal opacity-0 translate-y-6 transition-all duration-700 ease-out flex flex-col ${
                  index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
                } gap-8 lg:gap-12 items-stretch`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                {/* Image */}
                <div className="lg:w-1/2 flex flex-col">
                  <div className="rounded-2xl overflow-hidden aspect-[4/3] border border-dark-border">
                    <img
                      src={pkg.image}
                      alt={pkg.name}
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                  </div>

                  {/* NSF Certified link — Origin only, under image */}
                  {pkg.id === "origin" && (
                    <button
                      type="button"
                      onClick={() => setNsfModalOpen(true)}
                      className="mt-3 flex w-fit items-center gap-1.5 text-xs text-charcoal-500 font-sans hover:text-gold transition-colors duration-300 cursor-pointer"
                    >
                      <i className="ri-shield-check-line text-xs"></i>
                      NSF certified
                    </button>
                  )}
                </div>

                {/* Content */}
                <div className="lg:w-1/2 flex flex-col justify-center">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-semibold text-charcoal-50 leading-tight">
                        {pkg.name}
                      </h2>
                    </div>
                    <p className="text-lg text-charcoal-300 font-sans italic mb-4">
                      {pkg.tagline}
                    </p>
                    {pkg.id === "diamond" ? (
                      <p className="font-serif text-3xl md:text-4xl font-semibold text-gold mb-6">
                        Inquire
                      </p>
                    ) : (
                      <div className="mb-6">
                        <p className="text-xs font-sans font-medium tracking-[0.15em] text-charcoal-400 uppercase mb-1">
                          Starting at
                        </p>
                        <p className="font-serif text-3xl md:text-4xl font-semibold text-gold">
                          ${pkg.price.toLocaleString()}
                        </p>
                      </div>
                    )}
                    {pkg.description && (
                      <p className="text-base text-charcoal-300 font-sans leading-relaxed mb-6">
                        {pkg.description}
                      </p>
                    )}

                    {/* Features List */}
                    {pkg.features.length > 0 && (
                      <ul className="flex flex-col gap-3 mb-8">
                        {pkg.features.map((feature, i) => (
                          <li key={i} className="flex items-start gap-3 text-sm text-charcoal-200 font-sans">
                            {feature}
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* CTA */}
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gold text-dark-bg text-sm font-sans font-semibold rounded-full hover:bg-gold-light transition-colors duration-300 whitespace-nowrap"
                    >
                      Inquire About {pkg.tier}
                      <i className="ri-arrow-right-line text-xs"></i>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Policy Section */}
        <section className="pb-24 md:pb-32 bg-dark-bg">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-3xl">
            <div className="flex items-start gap-5 border-t border-dark-border pt-10 md:pt-12">
              <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0 border border-gold/20 mt-1">
                <i className="ri-shield-check-line text-gold"></i>
              </div>
              <div>
                <h3 className="font-serif text-lg font-semibold text-charcoal-50 mb-3">
                  Subscription Policy
                </h3>
                <p className="text-sm text-charcoal-300 font-sans leading-relaxed">
                  Purchases are refundable until installation begins. Once install begins, refunds are not allowed. Subscriptions can be canceled at any time with 30 days' notice.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* NSF Certified Claims Modal */}
      {nsfModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setNsfModalOpen(false)}
        >
          <div
            className="w-full max-w-lg bg-[#1c1c1e] border border-dark-border rounded-2xl p-6 md:p-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-xl md:text-2xl font-semibold text-charcoal-50">
                NSF Certified Reduction Claims
              </h3>
              <button
                type="button"
                onClick={() => setNsfModalOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-charcoal-700/50 text-charcoal-300 hover:text-charcoal-50 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <p className="text-sm text-charcoal-400 font-sans mb-5">
              The Origin is independently tested and certified to NSF/ANSI standards for the following contaminant reductions:
            </p>
            <ul className="flex flex-col gap-2.5 max-h-[60vh] overflow-y-auto pr-1">
              {nsfClaims.map((claim, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-gold text-xs"></i>
                  </div>
                  <span className="text-sm text-charcoal-200 font-sans">{claim}</span>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => setNsfModalOpen(false)}
              className="mt-6 w-full py-3 bg-gold text-dark-bg text-sm font-sans font-semibold rounded-full hover:bg-gold-light transition-colors cursor-pointer whitespace-nowrap"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}